// SEO-optimized service landing pages — data-driven
// Each entry powers a /services/<slug> route via ServiceLanding.jsx
// Structured for Google + AI search (Perplexity, ChatGPT) crawlers.

export const SEO_SERVICES = {
  "soft-fm": {
    eyebrow: "Soft FM Services · UAE",
    h1: "Soft Facility Management Services in UAE",
    metaTitle: "Soft FM Services in Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Integrated soft FM — daily cleaning, pest control, landscaping, waste management & day porters for commercial, residential & industrial sites across the UAE.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/lkbu36gp_WhatsApp%20Image%202026-08-26%20at%2010.11.25%20AM.jpeg",
    intro: "Soft FM covers the daily occupant-facing services that keep your facility presentable, hygienic and operational — cleaning, pest control, landscaping, waste handling and front-of-house manpower. Orion delivers all of it under a single SLA-driven contract across Dubai, Abu Dhabi, Sharjah and Ajman.",
    whatItIs: "Soft FM (Soft Facility Management) refers to the human-led, non-engineering services that maintain the everyday presentation, hygiene and comfort of a building.",
    whoItIsFor: "Building owners, property managers, OAs, commercial tenants, retail groups, schools, clinics and logistics operators who want predictable, reportable, single-vendor delivery.",
    benefits: [
      "One bill, one supervisor, one SLA — replaces 3-5 fragmented vendors",
      "Trained, uniformed, ID-badged staff sponsored by Orion",
      "Day-porter, night cleaning, weekend and surge crews available",
      "Eco-friendly, DM-approved chemicals & validated SOPs",
      "Live attendance tracking and monthly KPI reports",
    ],
    pricing: "Soft FM contracts in the UAE typically range from AED 4-12 per sqft/year depending on frequency, scope and site complexity. Orion provides a free site walkthrough and a fixed monthly fee — no padding, no hidden extras.",
    whyUs: [
      "Deep UAE FM delivery experience",
      "SLA-bound response times measured monthly",
      "Specialist coverage: HACCP kitchens, DHA clinics, KHDA schools",
      "72-hour manpower surge capability for events & peak seasons",
    ],
    scope: [
      "Daily housekeeping and day-porter services",
      "Toilet hygiene, restock and odour control",
      "Glass, marble and high-bay cleaning",
      "Pest control with DM-approved chemicals",
      "Landscape, irrigation and outdoor maintenance",
      "Waste segregation and disposal coordination",
      "Pantry, mess-room and kitchen deep cleans",
      "Special event mobilization",
    ],
    faqs: [
      { q: "What's the difference between hard and soft FM?", a: "Hard FM covers building systems (HVAC, MEP, electrical, fire). Soft FM covers occupant-facing services (cleaning, pest, landscape, waste). Orion delivers both under one contract." },
      { q: "Can I scale soft FM up or down monthly?", a: "Yes. Our contracts include flex clauses for peak season and event manpower — surge up to 500 staff in 72 hours." },
      { q: "Are your cleaning chemicals safe for healthcare and food sites?", a: "Yes. We use HACCP-grade products for F&B and validated DHA/DOH-approved chemicals for healthcare." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "hard-fm": {
    eyebrow: "Hard FM Services · UAE",
    h1: "Hard Facility Management & MEP Services in UAE",
    metaTitle: "Hard FM Services UAE | HVAC, MEP, Electrical & Plumbing | Orion FM",
    metaDesc: "SLA-bound hard FM — HVAC, MEP, electrical, plumbing, fire & life safety PPM across Dubai, Abu Dhabi, Sharjah & Ajman. 24/7 emergency response.",
    image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Hard FM keeps your building's critical engineering systems running — HVAC, MEP, electrical, plumbing, fire and life safety. Orion's certified technicians deliver preventive maintenance, emergency response and compliance under SLA contracts across the UAE.",
    whatItIs: "Hard FM (Hard Facility Management) refers to the engineering services that maintain the physical infrastructure of a building — systems your occupants rely on but rarely see.",
    whoItIsFor: "Asset owners, property managers, logistics operators and commercial tenants who need uptime, certified compliance and predictable monthly OPEX.",
    benefits: [
      "Preventive PPM calendar covering HVAC, MEP, fire, electrical, plumbing",
      "DEWA, ADDC and SEWA-registered technicians",
      "24/7 emergency response with SLA-bound times (30 min critical)",
      "Civil Defence-aligned fire & life-safety compliance",
      "Asset register, monthly KPI reports and quarterly business reviews",
    ],
    pricing: "Hard FM contracts in the UAE typically range from AED 6-18 per sqft/year, depending on asset age and system complexity. Orion offers free benchmarking against your current spend.",
    whyUs: [
      "Certified DEWA, ADDC, SEWA and Civil Defence engineers",
      "BMS, chiller, FCU and split-AC PPM at scale",
      "Single SPOC and consolidated monthly billing",
      "Same-day mobilization for emergencies anywhere in the UAE",
    ],
    scope: [
      "HVAC servicing, descaling and chiller PPM",
      "MEP (mechanical, electrical, plumbing) preventive maintenance",
      "Fire alarm, sprinkler and emergency lighting PPM",
      "BMS / FCU sensor calibration and tuning",
      "Generator, UPS and load-bank testing",
      "Cooling tower legionella testing",
      "Lift, escalator and access-control AMC",
      "Reactive emergency callouts 24/7",
    ],
    faqs: [
      { q: "How often should HVAC be serviced in UAE buildings?", a: "Commercial HVAC needs monthly PPM. Residential split-AC needs quarterly servicing. UAE dust and 50C summers accelerate wear significantly." },
      { q: "Are your engineers DEWA & Civil Defence certified?", a: "Yes. All Orion engineering staff are sponsored by us and hold the relevant DEWA, ADDC, SEWA or Civil Defence approvals." },
      { q: "What's your emergency response SLA?", a: "30 minutes for critical (life-safety, flooding), 2 hours for major, 8 hours for minor — measured and reported monthly." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "deep-cleaning-uae": {
    eyebrow: "Deep Cleaning · UAE",
    h1: "Professional Deep Cleaning Services in UAE",
    metaTitle: "Deep Cleaning Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "One-time, quarterly and post-event deep cleaning for villas, apartments, offices, kitchens and warehouses across the UAE. DM-licensed teams, eco-friendly chemicals.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/a1ifbilq_WhatsApp%20Image%202026-04-22%20at%2012.26.03%20PM%20%283%29.jpeg",
    intro: "Deep cleaning is more than a regular clean — it's a top-to-bottom reset of your space using specialist equipment, validated chemicals and trained crews. Orion deep-cleans villas, apartments, offices, restaurants and warehouses across Dubai, Abu Dhabi, Sharjah and Ajman.",
    whatItIs: "Deep cleaning is a comprehensive, intensive cleaning process that targets grime, build-up, and hard-to-reach areas missed by daily housekeeping — every surface, every corner, every appliance.",
    whoItIsFor: "Homeowners moving in/out, landlords resetting between tenants, restaurants meeting HACCP audits, offices post-renovation, and any operator preparing for a high-stakes inspection.",
    benefits: [
      "Steam, vacuum and chemical deep clean of every surface",
      "Kitchen degreasing, oven, hood and extract cleaning",
      "Bathroom descaling, regrouting and disinfection",
      "AC vent, duct, filter and split-unit cleaning",
      "Furniture, mattress, sofa and carpet shampooing",
    ],
    pricing: "Deep cleaning prices in the UAE start from AED 350 for a studio apartment, AED 800 for a 2-bed, AED 1,500+ for villas and AED 3 per sqft for commercial spaces. We provide a fixed quote after a 5-minute site call.",
    whyUs: [
      "DM-licensed and DHA-aligned chemicals",
      "Trained, uniformed, background-checked teams",
      "Same-day and weekend bookings available",
      "Satisfaction guarantee — we redo any missed area free",
    ],
    scope: [
      "Living room, bedrooms and corridors",
      "Kitchen, appliances, oven and extract",
      "Bathrooms, descaling and disinfection",
      "AC ducts, vents and filter cleaning",
      "Windows, glass, mirrors and balconies",
      "Carpets, sofas and mattress shampooing",
      "Tiled floor scrubbing and polishing",
      "Move-in / move-out reset packages",
    ],
    faqs: [
      { q: "How long does a deep cleaning take?", a: "A 1-bed apartment takes 3-4 hours, a villa 6-10 hours, an office 4-8 hours depending on size and condition." },
      { q: "Do you bring your own equipment and chemicals?", a: "Yes. Vacuums, steamers, scrubbers, ladders and all DM-approved chemicals are included in the price." },
      { q: "Is deep cleaning safe for kids and pets?", a: "Yes. We use eco-friendly, non-toxic chemicals on request. Just mention sensitivities when booking." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "hvac-maintenance-uae": {
    eyebrow: "HVAC Maintenance · UAE",
    h1: "HVAC Maintenance & AC Servicing in UAE",
    metaTitle: "HVAC Maintenance UAE | AC Servicing Dubai, Abu Dhabi | Orion FM",
    metaDesc: "Quarterly AC servicing, chiller PPM, ducting cleaning and gas recharge across the UAE. DEWA-certified engineers, AMC contracts available.",
    image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "UAE summers hit 50C — your HVAC system can't afford to fail. Orion provides preventive AC servicing, chiller PPM, ducting cleaning and emergency gas recharge for residential, commercial and industrial sites across Dubai, Abu Dhabi, Sharjah and Ajman.",
    whatItIs: "HVAC maintenance covers cleaning, inspection, calibration and repair of all heating, ventilation and air-conditioning systems — split ACs, central chillers, FCUs, AHUs and ducted systems.",
    whoItIsFor: "Homeowners, property managers, landlords, commercial tenants, warehouses and any UAE operator who cannot risk an AC failure in summer.",
    benefits: [
      "Quarterly PPM extends compressor life by 30-50%",
      "Cleaner coils cut electricity bills 15-25%",
      "Detect gas leaks before they shut down a unit",
      "Improve indoor air quality and reduce allergens",
      "DEWA-aligned compliance for commercial buildings",
    ],
    pricing: "Residential AC servicing starts from AED 99 per split unit. Annual AMC plans for villas start at AED 1,200. Commercial chiller PPM is quoted after a site visit.",
    whyUs: [
      "DEWA, ADDC, SEWA certified HVAC engineers",
      "Genuine spare parts from authorised distributors",
      "Same-day emergency callouts UAE-wide",
      "AMC plans with SLA-bound response times",
    ],
    scope: [
      "Split AC servicing, gas recharge and filter cleaning",
      "Central chiller PPM and descaling",
      "FCU and AHU maintenance",
      "Duct cleaning and sanitisation",
      "Cooling tower legionella testing",
      "BMS calibration and tuning",
      "Emergency leak, no-cool and breakdown response",
      "Annual maintenance contracts (AMC)",
    ],
    faqs: [
      { q: "How often should AC be serviced in UAE?", a: "Every 3 months for residential, monthly PPM for commercial. UAE dust and heat dramatically accelerate filter clogging and gas loss." },
      { q: "Do you handle central chillers and BMS?", a: "Yes. Our commercial team services York, Carrier, Trane, Daikin and McQuay chillers and integrates with major BMS platforms." },
      { q: "What's included in an HVAC AMC?", a: "Quarterly PPM, unlimited emergency callouts, filter replacements, gas top-up, and monthly performance reporting." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "plumbing-services-uae": {
    eyebrow: "Plumbing Services · UAE",
    h1: "Professional Plumbing Services in UAE",
    metaTitle: "Plumbing Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Emergency plumbing, leak detection, drainage and water tank cleaning across the UAE. Licensed plumbers, same-day callouts, 24/7 emergency.",
    image: "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "From a dripping tap to a burst pipe, Orion's licensed plumbers handle every plumbing need across Dubai, Abu Dhabi, Sharjah and Ajman — same-day callouts, 24/7 emergency, fixed pricing.",
    whatItIs: "Plumbing services cover the installation, repair and maintenance of water supply, drainage, sanitary fittings and water-heating systems in residential, commercial and industrial buildings.",
    whoItIsFor: "Homeowners, tenants, landlords, building managers, restaurants, hotels and any UAE property with a plumbing concern.",
    benefits: [
      "Same-day emergency response for leaks and blockages",
      "Licensed and insured plumbers — no cowboy work",
      "Leak detection using thermal imaging and acoustic tools",
      "Fixed pricing — no surprises after the job",
      "Warranty on all repairs and installations",
    ],
    pricing: "Minor plumbing repairs start from AED 150 callout + parts. Drainage cleaning from AED 250. Water tank cleaning AED 350-800 depending on size.",
    whyUs: [
      "Municipality-approved DM water tank cleaning certification",
      "24/7 emergency hotline UAE-wide",
      "Trained on all sanitary brands — Grohe, Hansgrohe, Toto, Kohler",
      "Insured against water damage claims",
    ],
    scope: [
      "Leak detection and pipe repair",
      "Drainage cleaning and unblocking",
      "Water tank cleaning (DM-certified)",
      "Sanitary fittings installation",
      "Water heater repair and installation",
      "Grease trap cleaning",
      "Pressure pump installation and PPM",
      "Bathroom and kitchen plumbing renovations",
    ],
    faqs: [
      { q: "How quickly can you reach me in an emergency?", a: "Within 60 minutes across Dubai, Sharjah and Ajman. 90 minutes for Abu Dhabi and outlying areas." },
      { q: "Do you provide DM-certified water tank cleaning?", a: "Yes. We are licensed by Dubai Municipality and issue compliance certificates after every cleaning." },
      { q: "Are your plumbers insured?", a: "Yes. All Orion plumbers carry public liability insurance covering water damage claims." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "electrical-services-uae": {
    eyebrow: "Electrical Services · UAE",
    h1: "Licensed Electrical Services in UAE",
    metaTitle: "Electrical Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "DEWA-licensed electrical works, fit-out wiring, fault finding and emergency repairs across Dubai, Abu Dhabi, Sharjah and Ajman.",
    image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Faulty wiring, tripped circuits, no-power emergencies — Orion's DEWA, ADDC and SEWA-licensed electricians handle residential and commercial electrical work across the UAE.",
    whatItIs: "Electrical services cover the design, installation, testing, repair and maintenance of all power, lighting and low-voltage systems in any building.",
    whoItIsFor: "Homeowners, fit-out contractors, building managers, retail chains, restaurants and any UAE operator who needs licensed electrical work.",
    benefits: [
      "DEWA, ADDC and SEWA-registered electricians",
      "Same-day fault-finding and emergency repair",
      "Lighting design and energy-efficient upgrades",
      "Earthing, RCD and load-balance testing",
      "Compliance certificates for handover and AMC",
    ],
    pricing: "Minor electrical repairs from AED 150 callout. Lighting installation AED 80-200 per point. Commercial fit-out quoted per site.",
    whyUs: [
      "Licensed by all UAE electrical authorities",
      "Insurance-backed work with written warranty",
      "Smart-home and KNX-trained engineers",
      "Same-day mobilization across the UAE",
    ],
    scope: [
      "Fault finding and emergency repair",
      "Lighting installation and design",
      "DB board upgrades and rewiring",
      "Earthing and RCD testing",
      "Smart-home and KNX installations",
      "Solar PV maintenance",
      "Generator and UPS installation",
      "Fit-out electrical works",
    ],
    faqs: [
      { q: "Are your electricians DEWA-licensed?", a: "Yes. All Orion electricians carry valid DEWA, ADDC or SEWA licenses depending on the emirate they operate in." },
      { q: "Can you handle smart-home installations?", a: "Yes. We're trained on KNX, Lutron, Crestron and the major smart-home protocols." },
      { q: "Do you issue compliance certificates?", a: "Yes. For commercial fit-outs and AMCs we issue DEWA-aligned compliance certificates after testing." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "handyman-services-uae": {
    eyebrow: "Handyman Services · UAE",
    h1: "On-Demand Handyman Services in UAE",
    metaTitle: "Handyman Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Painting, carpentry, mounting, repairs and small jobs handled by Orion's vetted handymen. Same-day bookings UAE-wide.",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "The shelf you never hung, the door that doesn't close, the wall that needs touching up — Orion's vetted handymen handle small jobs across the UAE with same-day booking and fixed pricing.",
    whatItIs: "Handyman services cover a broad range of small home and office maintenance jobs that don't require a specialist — painting touch-ups, carpentry, mounting, light repairs and assembly.",
    whoItIsFor: "Homeowners, tenants, landlords prepping a unit, and office managers who need quick fixes without booking multiple vendors.",
    benefits: [
      "One handyman handles multiple jobs in a single visit",
      "Fixed hourly or per-job pricing",
      "Vetted, uniformed and insured technicians",
      "Same-day and weekend bookings",
      "Free quotes before any work starts",
    ],
    pricing: "Hourly handyman service starts at AED 120/hour with a 2-hour minimum. Most jobs (3-5 tasks) finish in under 3 hours.",
    whyUs: [
      "Multi-skilled handymen — paint, wood, electrical, plumbing basics",
      "Insured for damage and accidents on site",
      "Same-day bookings via WhatsApp",
      "Backed by Orion's full FM team if a job needs escalation",
    ],
    scope: [
      "TV, shelf and curtain mounting",
      "Furniture assembly (IKEA, Home Centre, etc.)",
      "Minor painting and touch-ups",
      "Door, lock and hinge adjustments",
      "Tile regrouting and silicon resealing",
      "Light fixture and ceiling fan installation",
      "Minor plumbing fixes (taps, traps, seals)",
      "Picture hanging and wall mounting",
    ],
    faqs: [
      { q: "What's the minimum booking time?", a: "Two hours. Most clients fit 3-5 small jobs into that window." },
      { q: "Do I need to provide materials?", a: "For complex jobs (paint, tiles) we recommend you provide finishes — we'll bring all tools and consumables." },
      { q: "Can you handle electrical or plumbing jobs?", a: "For minor fixes yes. For DB board, rewiring or major plumbing we escalate to our licensed teams." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "snagging-services-uae": {
    eyebrow: "Snagging Services · UAE",
    h1: "Property Snagging & Handover Inspection in UAE",
    metaTitle: "Property Snagging Services Dubai & UAE | Orion FM",
    metaDesc: "Independent snagging inspections for new-build villas, apartments and townhouses across Dubai, Abu Dhabi and Sharjah. Detailed reports within 48 hours.",
    image: "https://images.unsplash.com/photo-1572025442646-866d16c84a54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Don't accept handover until you've snagged the defects. Orion's independent snagging inspectors check every surface, fitting and system in your new home or office — and give you a developer-ready report within 48 hours.",
    whatItIs: "Snagging is a professional inspection of a newly built or recently completed property to identify defects, finishing issues and incomplete works before you sign handover documents.",
    whoItIsFor: "First-time buyers, investors, property managers and corporate clients accepting handover of villas, apartments, townhouses, offices or fit-outs from developers or contractors.",
    benefits: [
      "Catch hundreds of defects before signing handover",
      "Force the developer to fix issues under warranty",
      "Independent — we don't work for the developer",
      "Detailed photo report with snag locations marked",
      "Re-inspection service to verify fixes",
    ],
    pricing: "Studio / 1-bed: AED 800. 2-bed: AED 1,000. 3-bed: AED 1,300. Villas from AED 1,800. Re-inspection 50% of original fee.",
    whyUs: [
      "Independent — never paid by developers",
      "DEWA, Civil Defence and plumbing-aware inspections",
      "Reports delivered in 48 hours with photos & locations",
      "Re-inspection to verify developer fixes",
    ],
    scope: [
      "Walls, floors, ceilings and finishes",
      "Joinery, doors, kitchen and wardrobes",
      "Bathrooms, sanitary and grouting",
      "Electrical points, switches and lighting",
      "AC operation and ducting",
      "Plumbing pressure and drainage",
      "Windows, balconies and external works",
      "Photographic report with snag locations marked",
    ],
    faqs: [
      { q: "When should I do a snagging inspection?", a: "Before you sign handover documents — that's your leverage with the developer. We recommend booking 7-14 days before your handover date." },
      { q: "Does the developer have to fix the snags?", a: "Under UAE law and most SPA agreements, developers must rectify defects within the warranty period. Our report gives you the documentation to enforce that." },
      { q: "Is re-inspection included?", a: "It's offered at 50% of the original fee to verify the developer has actually fixed everything in the report." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "airbnb-management-uae": {
    eyebrow: "Airbnb Management · UAE",
    h1: "Airbnb & Holiday Home Management in UAE",
    metaTitle: "Airbnb & Holiday Home Management Dubai & UAE | Orion FM",
    metaDesc: "Full-service Airbnb and short-let management — guest turnover cleaning, linen, maintenance and host support across Dubai, Abu Dhabi and Sharjah.",
    image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Hosting on Airbnb or Booking.com in Dubai? Orion handles the entire operational side — same-day turnover cleaning, fresh linen, maintenance call-outs and 5-star guest standards — so you can focus on bookings and revenue.",
    whatItIs: "Airbnb / holiday-home management is a hospitality-grade service that handles cleaning, linen, restocking, maintenance and guest issues between every short-term booking.",
    whoItIsFor: "DTCM-licensed hosts, property investors with one or many holiday-home units, and corporates managing serviced apartments in Dubai, Sharjah and Abu Dhabi.",
    benefits: [
      "Same-day check-out / check-in turnover cleaning",
      "Hotel-grade linen and towel service",
      "5-star presentation: bed-staging, amenity setup, scent",
      "Maintenance hotline for guest-reported issues",
      "Photo reports after every clean for hosts",
    ],
    pricing: "Studio turnover AED 120, 1-bed AED 160, 2-bed AED 220, villa AED 350+. Linen service available per kg or per bed-set.",
    whyUs: [
      "DTCM-aware operations (Dubai holiday home regulations)",
      "Hotel-trained housekeepers for guest-grade presentation",
      "Linen, amenities and consumables managed for you",
      "WhatsApp-based booking and reporting for hosts",
    ],
    scope: [
      "Same-day check-out / check-in turnover",
      "Hotel-grade linen and towel replacement",
      "Amenity kit setup (toiletries, water, coffee)",
      "Kitchen restock coordination",
      "AC, plumbing and electrical emergency callouts",
      "Photo report after each clean",
      "Deep clean monthly programs",
      "Welcome-pack and signature scent setup",
    ],
    faqs: [
      { q: "How fast is your turnover service?", a: "Most studio and 1-bed turnovers take 90 minutes. 2-bed under 2 hours. Villas 3-4 hours depending on size." },
      { q: "Do you handle DTCM compliance?", a: "We operate to DTCM holiday home standards but the rental license itself is your responsibility as host." },
      { q: "Can you handle maintenance issues from guests?", a: "Yes. Guests can WhatsApp our hotline directly — we respond within 60 minutes and dispatch a technician if needed." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah"],
  },

  "move-in-move-out-cleaning": {
    eyebrow: "Move-In / Move-Out Cleaning · UAE",
    h1: "Move-In & Move-Out Cleaning Services in UAE",
    metaTitle: "Move-In / Move-Out Cleaning Dubai & UAE | Orion FM",
    metaDesc: "Deposit-back move-out cleans, fresh move-in resets and landlord turnover cleaning across Dubai, Abu Dhabi, Sharjah and Ajman.",
    image: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/csdgqx8a_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%284%29.jpeg",
    intro: "Moving in or out of a UAE property? Get your deposit back, or move into a place that's been properly reset. Orion's move-in/out cleaning is built around landlord checklists and DM-approved chemicals.",
    whatItIs: "Move-in / move-out cleaning is a comprehensive end-of-tenancy clean designed to meet landlord handover standards or prepare a fresh property for new occupants.",
    whoItIsFor: "Tenants vacating to reclaim deposits, new tenants who want a sanitised home, landlords and property managers preparing units for the next lease.",
    benefits: [
      "Built around the standard UAE landlord handover checklist",
      "Deposit-back guarantee — we redo any missed area",
      "DM-licensed teams and approved chemicals",
      "Same-day and weekend bookings",
      "Photo report on completion",
    ],
    pricing: "Studio AED 350, 1-bed AED 550, 2-bed AED 750, 3-bed AED 950, villas from AED 1,500.",
    whyUs: [
      "Landlord-checklist-driven SOPs",
      "Insured and DM-licensed operations",
      "Free re-clean if anything is missed",
      "Coordination with moving companies and landlords",
    ],
    scope: [
      "Kitchen — oven, hood, fridge, cabinets, drawers",
      "Bathrooms — descaling, grout, mirror, fittings",
      "Bedrooms — wardrobe, skirting, fan, lights",
      "Living areas — vacuum, mop, dust all surfaces",
      "AC vents and filter cleaning",
      "Windows (internal) and balconies",
      "Wall scuff and mark cleaning",
      "Final photo report",
    ],
    faqs: [
      { q: "Will this cleaning get my deposit back?", a: "If your unit was in normal condition before, our move-out clean meets landlord standards. We offer a free re-clean if the landlord raises specific issues within 48 hours." },
      { q: "Should I be there during the clean?", a: "Ideally on arrival and departure so you can sign off. Otherwise, the building security can let us in if you coordinate." },
      { q: "How long does it take?", a: "A 1-bed takes 4-5 hours, a villa 8-10 hours. We send 2-3 cleaners per job to keep the timeline tight." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "office-cleaning-uae": {
    eyebrow: "Office Cleaning · UAE",
    h1: "Professional Office Cleaning Services in UAE",
    metaTitle: "Office Cleaning Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Daily, weekly and overnight office cleaning across Dubai, Abu Dhabi, Sharjah and Ajman. Uniformed, trained, DM-licensed teams.",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Your office is the first impression for clients, the daily environment for staff. Orion delivers daily, weekly and overnight office cleaning across the UAE — uniformed teams, DM-approved chemicals and a single supervisor per site.",
    whatItIs: "Office cleaning is a recurring janitorial service covering workstations, meeting rooms, kitchens, washrooms, common areas and pantry zones — usually scheduled before or after office hours.",
    whoItIsFor: "Office managers, HR teams, fit-out contractors handing over offices, co-working operators and SMEs that want presentable workspaces without the hassle of managing cleaners directly.",
    benefits: [
      "Daily, weekly or one-off frequencies — your call",
      "Off-hours cleaning to avoid workday disruption",
      "Dedicated supervisor per site, no churn",
      "Stocked consumables (tissues, soap, restocks) managed",
      "Monthly performance reports and live attendance tracking",
    ],
    pricing: "Office cleaning typically ranges from AED 3-8 per sqft/year for daily service. SMEs from AED 1,200/month for weekly service. Quoted after a free site visit.",
    whyUs: [
      "Uniformed, ID-badged, DM-licensed staff",
      "Day-porter and night-shift options",
      "Coverage across all 7 emirates",
      "Backed by our full FM team if a fix-it is needed",
    ],
    scope: [
      "Workstation, desk and IT-friendly cleaning",
      "Meeting room reset between bookings",
      "Pantry, kitchen and break-room deep cleaning",
      "Washroom hygiene and restocking",
      "Glass partition and door cleaning",
      "Carpet vacuuming and spot treatment",
      "Waste collection and segregation",
      "Day-porter and reception support",
    ],
    faqs: [
      { q: "Do you provide cleaning supplies?", a: "Yes. All cleaning chemicals, equipment and consumables (tissues, hand soap, bin liners) are included in our quote unless you specifically want to provide your own." },
      { q: "Can you do off-hours cleaning?", a: "Yes. Most of our office clients are cleaned between 8 PM and 6 AM to avoid any workday disruption." },
      { q: "Do you handle small offices (under 500 sqft)?", a: "Yes. We have weekly and bi-weekly packages designed for small offices, co-working pods and serviced offices." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "villa-maintenance-uae": {
    eyebrow: "Villa Maintenance · UAE",
    h1: "Villa Maintenance & AMC Services in UAE",
    metaTitle: "Villa Maintenance Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Comprehensive villa AMC — AC, plumbing, electrical, carpentry, painting and handyman under one annual contract across the UAE.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/md7xadxo_WhatsApp%20Image%202026-04-22%20at%2012.26.03%20PM%20%284%29.jpeg",
    intro: "Your villa is your biggest asset — don't let small issues become big bills. Orion's villa AMC bundles AC, plumbing, electrical, carpentry and handyman work into one annual contract with a single point of contact.",
    whatItIs: "A villa AMC (Annual Maintenance Contract) is an all-inclusive yearly maintenance plan covering preventive servicing, emergency callouts and routine repairs for your home.",
    whoItIsFor: "Villa owners and long-term tenants in Dubai, Abu Dhabi, Sharjah and Ajman who want predictable maintenance costs and one number to call for any issue.",
    benefits: [
      "One contract covers AC, plumbing, electrical, carpentry, painting",
      "Quarterly preventive AC servicing included",
      "24/7 emergency callouts UAE-wide",
      "Fixed annual cost — no surprise invoices",
      "Dedicated supervisor and WhatsApp hotline",
    ],
    pricing: "Standard 3-bed villa AMC from AED 3,500/year. 4-bed from AED 4,500. 5-bed from AED 5,500. Premium plans include painting and consumables.",
    whyUs: [
      "DEWA, ADDC, SEWA-certified technicians",
      "Same-day response for emergencies",
      "Branded vehicles and uniformed teams",
      "Insured for damage and workmen's comp",
    ],
    scope: [
      "Quarterly AC servicing (all splits)",
      "Plumbing repairs and leak detection",
      "Electrical fault-finding and repair",
      "Carpentry, doors and joinery fixes",
      "Painting touch-ups (small areas)",
      "Garden, pool and BBQ-area maintenance (premium)",
      "Pest control coordination",
      "24/7 emergency hotline",
    ],
    faqs: [
      { q: "What's covered in a villa AMC?", a: "Quarterly AC servicing, unlimited emergency callouts, minor plumbing/electrical/carpentry repairs and 24/7 hotline. Parts above AED 200 are charged at cost." },
      { q: "Can I add pool, garden or pest control?", a: "Yes. Our premium AMC includes pool chemistry, weekly garden maintenance and monthly pest control." },
      { q: "How fast is emergency response?", a: "Within 60 minutes across Dubai, Sharjah and Ajman. 90 minutes for Abu Dhabi." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "annual-maintenance-contract": {
    eyebrow: "AMC · UAE",
    h1: "Annual Maintenance Contracts (AMC) in UAE",
    metaTitle: "Annual Maintenance Contract UAE | AMC Plans Dubai | Orion FM",
    metaDesc: "Annual maintenance contracts for villas, offices, retail and warehouses across the UAE. SLA-bound response, fixed monthly fees, 24/7 emergency.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/ro6meixl_WhatsApp%20Image%202026-09-05%20at%208.51.12%20PM%20%281%29.jpeg",
    intro: "An AMC turns unpredictable maintenance costs into one fixed monthly fee with SLA-backed response times. Orion offers AMCs for villas, offices, retail outlets and warehouses across Dubai, Abu Dhabi, Sharjah and Ajman.",
    whatItIs: "An Annual Maintenance Contract is a 12-month agreement that bundles preventive maintenance, emergency callouts, account management and reporting into a fixed periodic fee.",
    whoItIsFor: "Villa owners, office tenants, retail chains, warehouse operators and any UAE entity that wants predictable OPEX, guaranteed uptime and a single accountable maintenance partner.",
    benefits: [
      "Fixed monthly fee — predictable OPEX, no surprise invoices",
      "SLA-bound response times (30 min critical, 2 hr major, 8 hr minor)",
      "Quarterly PPM on AC, plumbing, electrical, fire",
      "Dedicated account manager and single point of contact",
      "Monthly performance reports and quarterly business reviews",
    ],
    pricing: "Villa AMC from AED 3,500/year. Office AMC from AED 8,000/year. Retail outlet AMC from AED 12,000/year. Warehouse AMC quoted per site.",
    whyUs: [
      "Deep UAE FM experience",
      "DEWA, ADDC, SEWA and Civil Defence certified",
      "Coverage across all 7 emirates",
      "Transparent monthly reporting on every callout",
    ],
    scope: [
      "Scheduled PPM calendar (HVAC, MEP, fire, electrical)",
      "Unlimited emergency callouts within SLA",
      "Dedicated supervisor and account manager",
      "Monthly KPI reports and asset register",
      "Quarterly business reviews",
      "Civil Defence and fire compliance support",
      "Spare parts management",
      "HSE compliance and insurance coverage",
    ],
    faqs: [
      { q: "What's the minimum AMC contract length?", a: "12 months. Some clients run 24 or 36-month AMCs with locked-in pricing." },
      { q: "Are parts included in the AMC fee?", a: "Routine consumables (filters, basic fittings) are included. Major parts (compressors, pumps) are charged at cost with no markup." },
      { q: "Can I cancel mid-contract?", a: "Yes, with 60 days notice. Pro-rata refund of unused PPM visits." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "post-construction-cleaning": {
    eyebrow: "Post-Construction Cleaning · UAE",
    h1: "Post-Construction & Handover Cleaning in UAE",
    metaTitle: "Post-Construction Cleaning Services Dubai & UAE | Orion FM",
    metaDesc: "Builder's clean, sparkle clean and final handover cleaning for new villas, apartments, offices and fit-outs across the UAE.",
    image: "https://customer-assets.emergentagent.com/job_orion-integrated-fm/artifacts/xvtqga3y_WhatsApp%20Image%202026-06-20%20at%208.31.33%20PM%20%281%29.jpeg",
    intro: "Construction dust gets everywhere — and clients won't sign handover until it's gone. Orion delivers builder's clean, sparkle clean and final handover cleaning for villas, apartments, offices and retail fit-outs across the UAE.",
    whatItIs: "Post-construction cleaning is a three-stage process — rough clean (during build), builder's clean (after main works) and sparkle clean (before handover) — that removes dust, paint, adhesive and debris from a newly completed property.",
    whoItIsFor: "Developers, fit-out contractors, interior designers, property owners and project managers responsible for handover of newly built or refurbished spaces.",
    benefits: [
      "Three-stage process: rough, builder's, sparkle",
      "Specialist equipment for dust extraction",
      "Removes paint splash, adhesive, label residue, grout haze",
      "Snag-list aware — works alongside snagging inspectors",
      "Civil Defence and handover-ready finish",
    ],
    pricing: "Studio AED 800, 1-bed AED 1,200, 2-bed AED 1,800, villa AED 3,500+. Commercial fit-outs from AED 5 per sqft.",
    whyUs: [
      "Trained on builder's clean SOPs and grout-haze removal",
      "HEPA vacuum and dust-extraction equipment",
      "Coordination with developers and fit-out contractors",
      "Scaled crews (10-30 staff) for large projects",
    ],
    scope: [
      "Dust extraction across all surfaces",
      "Paint, adhesive and label residue removal",
      "Grout haze and tile residue cleaning",
      "Window, frame and balcony cleaning",
      "Kitchen, appliance and cabinet wipe-down",
      "Bathroom descaling and polishing",
      "AC vent and duct cleaning",
      "Final sparkle clean before handover",
    ],
    faqs: [
      { q: "When should we book post-construction cleaning?", a: "Schedule the builder's clean as soon as main works finish, and the sparkle clean 24-48 hours before handover." },
      { q: "Do you remove paint splash and grout haze?", a: "Yes. These are the two hardest residues and we use specialist chemicals to remove them without damaging finishes." },
      { q: "Can you handle large commercial fit-outs?", a: "Yes. We've cleaned offices and retail units up to 50,000 sqft with 30-strong crews running shifts." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "pest-control-uae": {
    eyebrow: "Pest Control · UAE",
    h1: "Professional Pest Control Services in UAE",
    metaTitle: "Pest Control Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Municipality-approved pest control for cockroaches, bed bugs, termites, rodents and ants across the UAE. Residential and commercial.",
    image: "https://images.unsplash.com/photo-1580745312924-15cb7e92f5f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Cockroaches, bed bugs, termites, rodents — UAE heat and humidity are pest paradise. Orion provides municipality-approved pest control for homes, restaurants, offices and warehouses with same-day appointments.",
    whatItIs: "Pest control is the prevention, detection and elimination of pests using chemical, mechanical and integrated pest management (IPM) techniques.",
    whoItIsFor: "Homeowners, tenants, restaurants and F&B operators (HACCP audits), offices, warehouses, schools and clinics with municipal compliance requirements.",
    benefits: [
      "Municipality-approved chemicals and methods",
      "HACCP and DM-compliant SOPs for F&B clients",
      "Same-day and weekend appointments",
      "Free re-treatment if pests return within warranty",
      "Discreet, no-fumigation odour residue options",
    ],
    pricing: "General pest control (apartments) AED 200-400. Bed bug treatment AED 400-1,000. Termite barrier AED 800-2,500. Commercial quoted per visit.",
    whyUs: [
      "Dubai Municipality and ADM-licensed",
      "HACCP-compliant for restaurants and food sites",
      "Trained on Integrated Pest Management (IPM)",
      "Free re-treatment warranty",
    ],
    scope: [
      "Cockroach gel and spray treatment",
      "Bed bug heat and chemical treatment",
      "Termite pre/post-construction barriers",
      "Rodent baiting and trapping",
      "Ant, silverfish and millipede control",
      "Mosquito and fly control",
      "Wasp and bee removal",
      "F&B HACCP pest management contracts",
    ],
    faqs: [
      { q: "Are your chemicals safe for kids and pets?", a: "Yes. We use municipality-approved chemicals with low-toxicity for residential settings and we'll advise on safe re-entry times." },
      { q: "Do you handle bed bugs?", a: "Yes — we offer both chemical and heat treatments. Heat treatment is more effective and chemical-free." },
      { q: "How long is the warranty?", a: "30 days for general pest, 90 days for bed bugs, 12 months for termite barriers. Free re-treatment if pests return." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "project-management-uae": {
    eyebrow: "Project Management · UAE",
    h1: "Fit-Out & FM Project Management in the UAE",
    metaTitle: "Fit-Out & Project Management Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "End-to-end fit-out, refurbishment and building-services project management across Dubai, Abu Dhabi, Sharjah and Ajman — scope, budget, schedule, HSE and handover.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/uj274oin_WhatsApp%20Image%202026-09-05%20at%208.54.08%20PM.jpeg",
    intro: "Orion delivers turn-key project management for office fit-outs, retail refurbishments, MEP upgrades and building-services replacements across the UAE — one accountable team from concept to handover.",
    whatItIs: "Project management is the professional planning, scheduling, cost-control, safety oversight and handover of construction, fit-out and building-services works from concept to occupancy.",
    whoItIsFor: "Landlords, tenants, fit-out companies, retail chains and building owners who need a single accountable project lead for renovations, MEP upgrades or new-site mobilisation.",
    benefits: [
      "One project lead across scope, budget, schedule, HSE and handover",
      "DEWA / Civil Defence NOC coordination handled end-to-end",
      "Weekly progress reports with photos, RAG status and risk log",
      "Snag-driven handover — you sign only after every defect is closed",
      "Integrates directly with our FM & AMC contracts post-handover",
    ],
    pricing: "Project management fees typically 6-10% of project value for fit-outs > AED 250k, or a fixed monthly fee for phased programmes. Free scoping call to quote.",
    whyUs: [
      "PMP / PRINCE2-trained project leads",
      "UAE authority coordination (DEWA, ADDC, SEWA, Civil Defence, DM)",
      "IRATA + Civil Defence-trained safety supervisors",
      "Seamless handover into Orion FM/AMC — no vendor gap",
    ],
    scope: [
      "Feasibility, scope and budget definition",
      "Contractor selection and tendering",
      "Design coordination (MEP, ID, structural)",
      "Programme scheduling and critical-path tracking",
      "HSE plan and site safety supervision",
      "DEWA / Civil Defence NOC and permits",
      "Snagging, DLP and handover documentation",
      "Post-handover FM/AMC mobilisation",
    ],
    faqs: [
      { q: "What size projects do you handle?", a: "From single-office fit-outs (~AED 100k) up to multi-floor commercial refurbishments and warehouse mobilisations (~AED 10m+)." },
      { q: "Do you charge a % of project value?", a: "Typically 6-10% depending on scope and duration. For long programmes we offer a fixed monthly PM fee." },
      { q: "Can Orion also handle the FM contract after handover?", a: "Yes — that's usually the point. We hand over directly into an Orion AMC so there's no vendor gap on day one." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },

  "disinfection-services-uae": {
    eyebrow: "Disinfection Services · UAE",
    h1: "Professional Disinfection & Sanitisation Services in UAE",
    metaTitle: "Disinfection Services Dubai, Abu Dhabi & UAE | Orion FM",
    metaDesc: "Hospital-grade disinfection and sanitisation for offices, schools, clinics, retail and homes across the UAE. ULV fogging, surface disinfection, validated chemicals.",
    image: "https://images.unsplash.com/photo-1584467735815-f778f274e296?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "When you need certainty — after illness, before a high-stakes event, or as routine infection control — Orion delivers hospital-grade disinfection using ULV fogging, surface protocols and Dubai Municipality-approved chemicals.",
    whatItIs: "Disinfection is the application of chemical or physical agents to surfaces and air to destroy pathogens including viruses, bacteria, fungi and spores.",
    whoItIsFor: "Clinics, schools (KHDA/ADEK aligned), offices post-illness, restaurants meeting HACCP audits, homes with vulnerable occupants and any operator wanting documented infection control.",
    benefits: [
      "Hospital-grade chemicals tested on viruses and bacteria",
      "ULV (Ultra Low Volume) fogging for full coverage",
      "DM-approved and DHA/DOH-aligned chemicals",
      "Certificates of disinfection issued on completion",
      "Same-day emergency response",
    ],
    pricing: "Studio AED 250, 1-bed AED 400, 2-bed AED 600, villa AED 1,200+. Office and commercial AED 1.5-3 per sqft.",
    whyUs: [
      "Validated chemicals tested on COVID-19, MRSA, influenza",
      "ULV fogging, surface and electrostatic spray options",
      "Trained on PPE and waste management",
      "Disinfection certificate issued for compliance",
    ],
    scope: [
      "ULV fogging across entire space",
      "Surface disinfection (touch-points, desks, electronics)",
      "HVAC vent and duct sanitisation",
      "Bathroom and washroom deep disinfection",
      "Kitchen and pantry sanitisation",
      "Soft furnishing fogging (sofas, curtains)",
      "Validated chemical residue testing",
      "Certificate of disinfection on completion",
    ],
    faqs: [
      { q: "How long until we can re-enter the space?", a: "Typically 2-4 hours after fogging. We give exact re-entry times based on the chemical used and space ventilation." },
      { q: "Are the chemicals safe for kids and asthma sufferers?", a: "Yes. We can use eco-friendly, non-aerosol surface disinfection for sensitive occupants." },
      { q: "Do you issue a disinfection certificate?", a: "Yes. For commercial and clinic clients we issue a numbered certificate with chemicals used and re-entry times." },
    ],
    relatedAreas: ["dubai", "abu-dhabi", "sharjah", "ajman"],
  },
};

export const SEO_SERVICE_SLUGS = Object.keys(SEO_SERVICES);

export const AREA_DETAILS = {
  dubai: {
    name: "Dubai",
    nameLong: "Dubai, UAE",
    metaTitle: "Facility Management & Cleaning Services in Dubai | Orion FM",
    metaDesc: "Integrated FM, cleaning, maintenance and AMC services across Dubai — Business Bay, Downtown, JLT, Marina, JAFZA, DIP, Al Quoz and more.",
    image: "https://images.unsplash.com/photo-1546412414-e1885259563a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Orion delivers integrated facility management, cleaning, maintenance and AMC services across all of Dubai — from Grade-A towers in Business Bay and DIFC, to warehouses in JAFZA and DIP, to villa communities in Arabian Ranches and Emirates Hills.",
    coverage: [
      "Business Bay & Downtown",
      "DIFC & Sheikh Zayed Road",
      "Dubai Marina, JLT & JBR",
      "JAFZA, DP World & Jebel Ali",
      "DIP, Al Quoz & Dubai Investment Park",
      "Arabian Ranches, Emirates Hills, Meadows",
      "Mirdif, Al Barsha, Al Khawaneej",
      "Deira, Bur Dubai, Karama",
    ],
    sectors: ["Logistics & 3PL warehouses", "Grade-A commercial towers", "Retail, malls & F&B", "Residential communities", "Healthcare clinics", "Schools & universities"],
  },
  "abu-dhabi": {
    name: "Abu Dhabi",
    nameLong: "Abu Dhabi, UAE",
    metaTitle: "Facility Management & Cleaning Services in Abu Dhabi | Orion FM",
    metaDesc: "Integrated FM, cleaning, maintenance and AMC services across Abu Dhabi — Corniche, Al Reem, Saadiyat, Yas, KIZAD, Mussafah and more.",
    image: "https://images.unsplash.com/photo-1535320903710-d993d3d77d29?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Orion serves Abu Dhabi's commercial, residential and industrial operators — from KIZAD warehouses, to ADGM towers, to Saadiyat villas. ADDC-certified engineers and DOH-aligned cleaning teams.",
    coverage: [
      "Corniche & Al Markaziyah",
      "Al Reem Island & Al Maryah",
      "Saadiyat Island & Yas Island",
      "KIZAD & Khalifa Industrial Zone",
      "Mussafah & Industrial Area",
      "Khalifa City, Mohammed Bin Zayed",
      "ADGM & Sowwah Square",
      "Bani Yas, Shahama, Al Ain",
    ],
    sectors: ["Industrial & KIZAD operators", "ADGM & financial offices", "Hotels & hospitality", "Residential communities", "Government & semi-government", "Healthcare & DOH"],
  },
  sharjah: {
    name: "Sharjah",
    nameLong: "Sharjah, UAE",
    metaTitle: "Facility Management & Cleaning Services in Sharjah | Orion FM",
    metaDesc: "Integrated FM, cleaning, maintenance and AMC services across Sharjah — Industrial Areas, Al Majaz, Al Khan, SAIF Zone, Hamriyah and more.",
    image: "https://images.unsplash.com/photo-1564352969906-8b7f46ba4b8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Orion serves Sharjah's industrial backbone — SAIF Zone, Hamriyah, Industrial Areas 1-18 — plus residential and commercial sites along Al Majaz and Al Khan. SEWA-certified engineering teams.",
    coverage: [
      "SAIF Zone & Hamriyah Free Zone",
      "Industrial Areas 1-18",
      "Al Majaz & Al Khan",
      "Al Nahda & Al Taawun",
      "Al Qasimia & Al Wahda",
      "Muweilah & University City",
      "Al Rahmaniya & Tilal City",
    ],
    sectors: ["Manufacturing & industrial", "SAIF Zone tenants", "Residential communities", "Commercial offices", "Education & university campuses"],
  },
  ajman: {
    name: "Ajman",
    nameLong: "Ajman, UAE",
    metaTitle: "Facility Management & Cleaning Services in Ajman | Orion FM",
    metaDesc: "Integrated FM, cleaning, maintenance and AMC services across Ajman — Ajman Free Zone, New Industrial Area, Al Nuaimiya, Corniche and more.",
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    intro: "Headquartered in Ajman Free Zone, Orion has deep local roots in Ajman — serving free-zone businesses, industrial operators, residential buildings and commercial offices across the emirate.",
    coverage: [
      "Ajman Free Zone (AFZA)",
      "New Industrial Area",
      "Al Nuaimiya & Al Rashidiya",
      "Corniche & City Centre",
      "Al Jurf & Al Hamidiyah",
      "Masfout & Manama",
    ],
    sectors: ["Free-zone businesses", "Industrial operators", "Residential buildings", "Commercial offices", "Hospitality"],
  },
};

export const AREA_SLUGS = Object.keys(AREA_DETAILS);
